import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Optional,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Subject, of } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    finalize,
    switchMap,
    take,
} from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { FlexSpacerDirective } from '../../shared/directives/flex-spacer.directive';
import {
    GableLesson,
    GableLessonsService,
} from '../../../../services/ai-poc/gable-lessons.service';
import { AssistantPocFeedbackChip } from '../../../../services/ai-poc/ai-poc.service';
import { SuperStarComponent } from '../../../app-layout/shared/super-star/super-star.component';
import { MarkdownPipe } from '../../../../pipes/markdown.pipe';
import { GableScreenshotUploaderComponent } from '../../../gable/gable-screenshot-uploader/gable-screenshot-uploader.component';
import { AlertBlockComponent } from '../../shared/alert-block/alert-block.component';
import {
    ConfirmActionV2DialogComponent,
    ConfirmActionV2DialogInput,
} from '../../shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';

export interface TeachGableDialogData {
    /** Question the user originally asked. Pinned read-only in the reference card. */
    userQuestion: string;
    /** Gable's last answer, behind a "Show Gable's answer" disclosure. */
    assistantAnswer: string;
    /**
     * Layperson-safe phase summaries captured during the turn. Shown
     * read-only alongside the assistant answer so the author sees what
     * Gable was thinking when it got things wrong.
     */
    reasoningTrail?: string[];
    /**
     * Which chip the user picked to get here (Wrong data / Missed nuance).
     * Stored alongside the lesson telemetry on save for analytics.
     */
    triggeringChip?: AssistantPocFeedbackChip;
    /** Edit mode — pass in an existing lesson to seed the form. */
    existingLesson?: GableLesson;
    /**
     * Draft seed for "create new lesson" flows (e.g. AI Activity →
     * "Turn this into a Gable lesson"). When `existingLesson` is unset
     * and this is present, the form starts pre-populated with these
     * fields so the author can refine and save instead of starting blank.
     */
    prefillDraft?: { title: string; triggerContext: string; guidance: string };
    /**
     * Stamp the lesson back to the source interaction event after save
     * (SD-6741 round 6). When set, the create call passes
     * `fromInteractionEventId` so the backend stamps
     * `ai_interaction_events.promoted_lesson_id` in the same
     * transaction — preventing the same answer from being promoted
     * into duplicate lessons.
     */
    fromInteractionEventId?: number;
}

export type TeachGableDialogResult =
    | { lesson: GableLesson; flagged?: boolean }
    | null;

interface TeachGableForm {
    title: FormControl<string>;
    triggerContext: FormControl<string>;
    guidance: FormControl<string>;
}

interface SimilarMatch {
    id: number;
    title: string;
    triggerContext: string;
    guidance: string;
    fireCount: number;
    lastFiredAt: string | null;
    similarity: number;
}

const GUIDANCE_MAX_CHARS = 1500;
const SIMILAR_MIN_TRIGGER_CHARS = 15;

/**
 * Teach Gable — right-edge sliding drawer (480px wide) for super admins
 * to author new domain rules in response to a bad Gable answer.
 *
 * Per design spec:
 *  - Header with "Super admin only" purple overline + Title + X
 *  - Reference card pinning the original question; "Show Gable's answer"
 *    disclosure for the answer body + reasoning trail
 *  - Trigger field NOT pre-filled (vector retrieval works better on
 *    conceptual descriptions than literal question phrasing)
 *  - Live similar-lessons callout (debounced poll against /lessons/similar)
 *  - Guidance with 1500-char counter; switches to error when exceeded
 *  - SQL example collapsed behind "+ Add SQL example (optional)"
 *  - Test block — calls /lessons/preview, renders new answer in green
 *  - Footer: Cancel · Flag for review · Save lesson
 *
 * The drawer is implemented as a MatDialog with a custom `gable-teach-drawer`
 * panel class so it slides in from the right edge full-height (handled
 * by global CSS in styles/ui/app.scss).
 */
@UntilDestroy()
@Component({
    selector: 'app-teach-gable-dialog',
    templateUrl: './teach-gable-dialog.component.html',
    styleUrls: ['./teach-gable-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        FlexSpacerDirective,
        SuperStarComponent,
        MarkdownPipe,
        GableScreenshotUploaderComponent,
        AlertBlockComponent,
    ],
})
export class TeachGableDialogComponent implements OnInit {
    /**
     * Embedded mode = inline in the Gable drawer (not a MatDialog overlay).
     * When true the component:
     *  - reads its data from `embeddedData` instead of MAT_DIALOG_DATA
     *  - emits via `closed` instead of calling MatDialogRef.close()
     *  - has its Cancel button render as a back-arrow in the host's chrome
     */
    @Input() embedded = false;

    /** Data input when used inline; matches the MAT_DIALOG_DATA shape. */
    @Input() embeddedData?: TeachGableDialogData;

    /** Emitted on save/flag/cancel when used inline. */
    @Output() readonly closed = new EventEmitter<TeachGableDialogResult>();

    readonly form: FormGroup<TeachGableForm>;
    readonly guidanceMaxChars = GUIDANCE_MAX_CHARS;

    isSaving = false;
    errorMessage: string | null = null;

    /** Show/hide the read-only Gable answer + reasoning under the reference card. */
    showAnswerDetails = false;

    /**
     * Screenshots the author is uploading in this session. Held as base64
     * data URLs in memory; sent to the backend on save where they get
     * vision-summarized and DROPPED — only the resulting text descriptions
     * land on the lesson row. So this array is always for *new* uploads,
     * never the lesson's previous screenshots.
     */
    pendingScreenshots: string[] = [];

    /**
     * Read-only summaries the backend produced from earlier uploads (when
     * editing an existing lesson). Cannot be edited in-place — to "remove"
     * a summary you'd have to add API for that.
     */
    existingScreenshotSummaries: string[] = [];

    /** Similar-lessons callout state. */
    similarMatches: SimilarMatch[] = [];
    isLoadingSimilar = false;
    /** Inline preview of the duplicate-match lesson (collapsed by default). */
    showDupPreview = false;
    /** Author dismissed the duplicate warning for the current trigger text. */
    private _dupDismissed = false;

    /** Test block state. */

    /** Retire (soft-delete) flow state. */
    isRetiring = false;

    /**
     * When the user pivots from "edit this lesson instead" inside the
     * duplicate warning, we swap the drawer into edit mode in-place. The
     * pivoted lesson is held here so `isEdit` and the submit/retire paths
     * pick it up the same way an outer-supplied `existingLesson` would.
     */
    private _overrideExisting: GableLesson | null = null;

    private readonly _triggerProbe$ = new Subject<string>();

    constructor(
        @Optional() private readonly _dialogRef: MatDialogRef<TeachGableDialogComponent, TeachGableDialogResult> | null,
        private readonly _fb: FormBuilder,
        private readonly _lessons: GableLessonsService,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _dialog: MatDialog,
        @Optional() @Inject(MAT_DIALOG_DATA) private readonly _injectedData: TeachGableDialogData | null,
    ) {
        // Resolved data: prefer the injected MAT_DIALOG_DATA when present
        // (dialog mode), fall back to the @Input when used inline. We can't
        // read `embeddedData` directly in the constructor because @Inputs
        // haven't been set yet, so we rely on a private getter and run
        // form setup in ngOnInit instead.
        const existing = this._injectedData?.existingLesson;
        // When there's no existing lesson, fall back to the optional
        // prefillDraft (used by "Turn this into a Gable lesson" from
        // AI Activity). prefillDraft is ignored in edit mode.
        const prefill = !existing ? this._injectedData?.prefillDraft : undefined;
        this.form = this._fb.nonNullable.group<TeachGableForm>({
            title: this._fb.nonNullable.control<string>(
                existing?.title ?? prefill?.title ?? '',
                [Validators.required, Validators.maxLength(200)],
            ),
            // Per spec: NEVER pre-fill with the literal user question.
            // Pre-filling encourages over-specific triggers; the original
            // question stays available in the reference card if needed.
            triggerContext: this._fb.nonNullable.control<string>(
                existing?.triggerContext ?? prefill?.triggerContext ?? '',
                [Validators.required, Validators.maxLength(2000)],
            ),
            guidance: this._fb.nonNullable.control<string>(
                existing?.guidance ?? prefill?.guidance ?? '',
                [Validators.required, Validators.maxLength(GUIDANCE_MAX_CHARS)],
            ),
        });

        if (existing?.screenshotSummaries?.length) {
            this.existingScreenshotSummaries = [...existing.screenshotSummaries];
        }

        // Block close on backdrop/escape unless explicit close handlers run.
        // Only applies in dialog mode (no dialogRef in embedded mode).
        if (this._dialogRef) {
            this._dialogRef.disableClose = true;
        }
    }

    /**
     * Resolved data — checks embedded @Input first (set on inline usage),
     * falls back to the injected MAT_DIALOG_DATA (dialog usage).
     */
    get data(): TeachGableDialogData {
        return this.embeddedData ?? this._injectedData ?? { userQuestion: '', assistantAnswer: '' };
    }

    ngOnInit(): void {
        // If we got data via @Input AFTER construction (embedded mode),
        // re-seed the form from it now.
        if (this.embedded && this.embeddedData?.existingLesson) {
            const existing = this.embeddedData.existingLesson;
            this.form.patchValue({
                title: existing.title ?? '',
                triggerContext: existing.triggerContext ?? '',
                guidance: existing.guidance ?? '',
            });
            if (existing.screenshotSummaries?.length) {
                this.existingScreenshotSummaries = [...existing.screenshotSummaries];
            }
        } else if (this.embedded && this.embeddedData?.prefillDraft) {
            const draft = this.embeddedData.prefillDraft;
            this.form.patchValue({
                title: draft.title ?? '',
                triggerContext: draft.triggerContext ?? '',
                guidance: draft.guidance ?? '',
            });
        }

        // Live similar-lessons callout — debounce 500ms, only fire when the
        // trigger text is long enough to be meaningful.
        this.form.controls.triggerContext.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                untilDestroyed(this),
            )
            .subscribe((value) => {
                // Trigger text changed — un-dismiss the duplicate warning and
                // re-collapse the preview so the next match starts fresh.
                this._dupDismissed = false;
                this.showDupPreview = false;

                if (!value || value.trim().length < SIMILAR_MIN_TRIGGER_CHARS) {
                    this.similarMatches = [];
                    this._cdr.markForCheck();
                    return;
                }
                this._triggerProbe$.next(value.trim());
            });

        this._triggerProbe$
            .pipe(
                switchMap((value) => {
                    this.isLoadingSimilar = true;
                    this._cdr.markForCheck();
                    return this._lessons.similar(value).pipe(
                        catchError(() => of({ matches: [] })),
                        finalize(() => {
                            this.isLoadingSimilar = false;
                            this._cdr.markForCheck();
                        }),
                    );
                }),
                untilDestroyed(this),
            )
            .subscribe(({ matches }) => {
                // Filter out the lesson currently being edited from the
                // matches list (editing a lesson shouldn't surface itself).
                const editingId = this.effectiveExisting?.id ?? null;
                this.similarMatches = matches.filter((m) => m.id !== editingId) as SimilarMatch[];
                this._cdr.markForCheck();
            });
    }

    // -------------------------------------------------------------------------
    // Form helpers
    // -------------------------------------------------------------------------

    get isEdit(): boolean {
        return !!this.effectiveExisting;
    }

    /**
     * Either the outer-supplied lesson (passed via MAT_DIALOG_DATA / inline
     * input from the admin row click) OR the lesson the user pivoted into
     * via "Edit this lesson instead" from the duplicate warning. Single
     * source of truth for "what lesson is this drawer mutating?" — used by
     * the submit path, the retire path, and the isEdit gate.
     */
    get effectiveExisting(): GableLesson | null {
        return this._overrideExisting ?? this.data.existingLesson ?? null;
    }

    /**
     * Top match for the duplicate warning. Surface only when we have a
     * result AND the user hasn't dismissed it for this trigger AND we're
     * not already editing it.
     */
    get bestMatch(): SimilarMatch | null {
        if (this._dupDismissed || this.similarMatches.length === 0) return null;
        const editingId = this.effectiveExisting?.id ?? null;
        const top = this.similarMatches.find((m) => m.id !== editingId);
        return top ?? null;
    }

    get guidanceLength(): number {
        return this.form.controls.guidance.value.length;
    }

    get guidanceOverLimit(): boolean {
        return this.guidanceLength > GUIDANCE_MAX_CHARS;
    }

    get canSave(): boolean {
        return this.form.valid && !this.isSaving && !this.guidanceOverLimit;
    }

    get submitLabel(): string {
        if (this.isSaving) return this.isEdit ? 'Saving…' : 'Teaching…';
        return this.isEdit ? 'Save changes' : 'Save lesson';
    }

    // -------------------------------------------------------------------------
    // UI handlers
    // -------------------------------------------------------------------------

    toggleAnswerDetails(): void {
        this.showAnswerDetails = !this.showAnswerDetails;
    }

    onSubmit(): void {
        this.submitInternal();
    }

    onCancel(): void {
        this._close(null);
    }

    /**
     * Unified close — dispatches to MatDialogRef.close() in dialog mode,
     * or emits via the `closed` Output in embedded mode. Lets the rest of
     * the component code stay mode-agnostic.
     */
    private _close(result: TeachGableDialogResult): void {
        if (this._dialogRef) {
            this._dialogRef.close(result);
        } else {
            this.closed.emit(result);
        }
    }

    trackByIndex(index: number): number {
        return index;
    }

    trackByMatchId(_idx: number, match: SimilarMatch): number {
        return match.id;
    }

    /**
     * Expand/collapse the inline preview of the duplicate-match lesson.
     */
    toggleDupPreview(): void {
        this.showDupPreview = !this.showDupPreview;
    }

    /**
     * "Keep my version" — collapse the duplicate preview and dismiss the
     * warning for the current trigger text. The warning will surface again
     * if the author edits the trigger.
     */
    keepMyVersion(): void {
        this.showDupPreview = false;
        this._dupDismissed = true;
    }

    /**
     * "Edit this lesson instead" — pivot the drawer into edit mode for the
     * matched lesson. If the author has typed anything into this draft we
     * confirm first; on confirm we swap the form values, set the override
     * existing-lesson so isEdit / submit / retire pick it up, and clear
     * the duplicate warning.
     */
    editThisLessonInstead(dup: SimilarMatch): void {
        const v = this.form.value;
        const hasUserInput = !!(v.title?.trim() || v.guidance?.trim());
        if (!hasUserInput) {
            this._swapToEditMode(dup);
            return;
        }
        const input: ConfirmActionV2DialogInput = {
            title: 'Discard the lesson you started?',
            content:
                "Anything you've written in this draft will be lost when we " +
                'switch to editing the existing lesson.',
            confirmButton: 'Discard and edit',
        };
        this._dialog
            .open(ConfirmActionV2DialogComponent, {
                panelClass: ['v2-dialog'],
                width: ConfirmActionV2DialogComponent.width,
                data: input,
            })
            .afterClosed()
            .pipe(take(1), untilDestroyed(this))
            .subscribe((result) => {
                if (!result?.success) return;
                this._swapToEditMode(dup);
            });
    }

    /**
     * Mutate the drawer into edit mode for the given lesson — patches the
     * form, sets `_overrideExisting`, and clears the duplicate warning.
     * Shared by the "no draft yet" and "confirmed discard" paths.
     */
    private _swapToEditMode(dup: SimilarMatch): void {
        // Build a transient GableLesson shape from the match payload —
        // the matches array already carries title/triggerContext/guidance
        // because the backend serializer returns the full lesson row.
        this._overrideExisting = {
            id: dup.id,
            title: dup.title,
            triggerContext: dup.triggerContext,
            guidance: dup.guidance,
            fireCount: dup.fireCount,
            lastFiredAt: dup.lastFiredAt,
        } as GableLesson;

        this.form.patchValue({
            title: dup.title,
            triggerContext: dup.triggerContext,
            guidance: dup.guidance,
        });
        this.form.markAsPristine();
        this.pendingScreenshots = [];
        this.existingScreenshotSummaries = [];
        this.showDupPreview = false;
        this._dupDismissed = true;
        this._cdr.markForCheck();
    }

    /**
     * Retire (soft-delete) the lesson currently being edited. Confirms
     * inline — Gable will stop using the lesson but the row stays in the
     * DB for audit. Only available in edit mode (button is hidden
     * otherwise via *ngIf="isEdit").
     */
    onRetire(): void {
        const target = this.effectiveExisting;
        if (!target || this.isRetiring) return;
        const input: ConfirmActionV2DialogInput = {
            title: `Retire "${target.title}"?`,
            content:
                'Retiring this lesson removes it from the pgvector index, so Gable ' +
                'will stop retrieving it when answering questions. The lesson row is ' +
                'kept with its fire history — you can reactivate it later to re-embed ' +
                'and put it back in rotation.',
            confirmButton: 'Retire lesson',
        };
        this._dialog
            .open(ConfirmActionV2DialogComponent, {
                panelClass: ['v2-dialog'],
                width: ConfirmActionV2DialogComponent.width,
                data: input,
            })
            .afterClosed()
            .pipe(take(1), untilDestroyed(this))
            .subscribe((result) => {
                if (!result?.success) return;
                this._performRetire(target.id);
            });
    }

    private _performRetire(id: number): void {
        this.isRetiring = true;
        this.errorMessage = null;
        this._cdr.markForCheck();

        this._lessons.retire(id).pipe(
            take(1),
            finalize(() => {
                this.isRetiring = false;
                this._cdr.markForCheck();
            }),
        ).subscribe({
            next: ({ lesson }) => this._close(<TeachGableDialogResult>{ lesson, flagged: false }),
            error: (err) => {
                this.errorMessage = this._extractError(err) ?? 'Could not retire the lesson. Please try again.';
            },
        });
    }

    // -------------------------------------------------------------------------
    // Submit (Save vs Flag share most of the logic — only `status` differs)
    // -------------------------------------------------------------------------

    private submitInternal(): void {
        if (!this.canSave) {
            this.form.markAllAsTouched();
            return;
        }
        const raw = this.form.getRawValue();
        const sourceEventId = this.isEdit ? undefined : this.data.fromInteractionEventId;
        const payload = {
            title: raw.title.trim(),
            triggerContext: raw.triggerContext.trim(),
            guidance: raw.guidance.trim(),
            // Send the raw uploads — backend will vision-summarize and
            // drop the originals before persisting. Sending `null` (not
            // an empty array) avoids touching existing summaries on an
            // edit-with-no-new-screenshots save.
            screenshotDataUrls: this.pendingScreenshots.length > 0
                ? this.pendingScreenshots
                : null,
            // When the dialog was opened from "Turn this into a Gable
            // lesson" on the AI Activity drawer, the source event id
            // gets stamped on the new lesson in the same DB
            // transaction so we can guard against duplicate promotions.
            ...(sourceEventId ? { fromInteractionEventId: sourceEventId } : {}),
        };
        this.isSaving = true;
        this.errorMessage = null;
        this._cdr.markForCheck();

        const obs = this.isEdit
            ? this._lessons.update(this.effectiveExisting!.id, payload)
            : this._lessons.create(payload);

        obs.pipe(
            take(1),
            finalize(() => {
                this.isSaving = false;
                this._cdr.markForCheck();
            }),
        ).subscribe({
            next: ({ lesson }) => this._close(<TeachGableDialogResult>{ lesson, flagged: false }),
            error: (err) => {
                this.errorMessage = this._extractError(err) ?? 'Could not save the lesson. Please try again.';
            },
        });
    }

    private _extractError(err: unknown): string | null {
        if (err && typeof err === 'object' && 'error' in err) {
            const inner = (err as { error?: { message?: string } }).error;
            if (inner?.message) return inner.message;
        }
        return null;
    }
}
